import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Button, Card, Banner } from '@superops/ui-design-system';
import { Loader } from '../../../index';
import {
  DeviceTypeSelector,
  EnrollmentTypeSelector,
  EnrollmentForm,
  QRCodeDisplay,
  EmailSender
} from './index';
import {
  useMDMEnrollment,
  useUserAssociations,
  usePolicySetMapping,
  useAdvancedHierarchy
} from '../hooks';
import { useMDMClientConfig } from '../../MDMClientSetupPage/hooks/useMDMClientConfig';
import { SiteSelector } from '../../MDMClientSetupPage/components/SiteSelector';
import { useClientSites } from '../../MDMClientSetupPage/hooks/useClientSites';
import { PlatformType, DeviceType, EnrollmentType } from '../types';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import InstructionStep from './InstructionStep';
import { FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { ASSET_VIEW_URL } from '../../../Constants';
import { useFlags } from 'launchdarkly-react-client-sdk';

export interface MDMManualEnrollmentProps {
  activeView: PlatformType;
  isConfigurationPage: boolean;
  client?: any;
  siteId?: string;
  showSiteSelector?: boolean;
}

const MDMManualEnrollment: React.FC<MDMManualEnrollmentProps> = (props) => {
  const tabContext = useContext(TabContext);
  const { macMdm } = useFlags();
  const [selectedDevice, setSelectedDevice] = useState<DeviceType>(
    macMdm ? 'Mac' : 'iPhone'
  );
  const [selectedPolicyCategory, setSelectedPolicyCategory] = useState<any>(
    null
  );
  const [enrollmentType, setEnrollmentType] = useState<EnrollmentType[]>([
    'fully-managed'
  ]);
  const [selectedDepartment, setSelectedDepartment] = useState<any>(null);

  const {
    activeView,
    isConfigurationPage,
    client,
    siteId: propSiteId,
    showSiteSelector = false
  } = props;

  const [selectedSiteId, setSelectedSiteId] = useState(propSiteId || '');

  // Use siteId from state if showSiteSelector is true, otherwise use prop
  const siteId = showSiteSelector ? selectedSiteId : propSiteId || '';

  // Custom hooks
  const { isAdvancedHierarchy, isMSPProduct } = useAdvancedHierarchy({
    client,
    clientId: client?.accountId
  });

  // Fetch site options if showSiteSelector is true
  const {
    siteOptions,
    loading: sitesLoading,
    error: sitesError
  } = useClientSites(client?.accountId || '');

  useEffect(() => {
    if (propSiteId) {
      setSelectedSiteId(propSiteId);
    }
  }, [propSiteId]);

  const {
    generateQRLoading,
    sendEmailLoading,
    enrollmentResponse,
    qrConfigData,
    showQRCard,
    qrLoading,
    generateEnrollmentQR,
    sendQRViaEmail,
    setQrLoading,
    resetQRDisplay
  } = useMDMEnrollment(isMSPProduct);

  const {
    userAssociations,
    selectedRequestor,
    setSelectedRequestor,
    associationsLoading
  } = useUserAssociations({ client, siteId, isMSPProduct, selectedDepartment });

  const classId = useMemo(() => {
    if (activeView === 'ANDROID') {
      return '41'; // Android
    }

    if (activeView === 'IOS') {
      switch (selectedDevice) {
        case 'iPhone':
          return '42'; // Apple iOS
        case 'iPad':
          return '43'; // Apple iPadOS
        case 'Mac':
          return '3'; // Apple macOS
        default:
          return '42'; // Default to iOS
      }
    }

    return undefined;
  }, [activeView, selectedDevice]);

  const {
    policySetMapping,
    loading: policySetMappingLoading
  } = usePolicySetMapping(client?.accountId, classId);

  const {
    clientConfig,
    hasAndroidConfig,
    hasIosConfig,
    loading: mdmConfigLoading,
    error: mdmConfigError,
    refetch: refetchMDMConfig
  } = useMDMClientConfig(client?.accountId || '');

  // Helper function to get the appropriate policy set mapping for the current selection
  const getCurrentPolicySetMapping = () => {
    if (!policySetMapping) return {};

    if (activeView === 'ANDROID') {
      return { android: policySetMapping.android };
    }

    if (activeView === 'IOS') {
      // CategoryAndDepartment expects 'ios' key regardless of device type
      // We filter the appropriate device data but return it with 'ios' key
      let deviceData = [];
      switch (selectedDevice) {
        case 'iPhone':
          deviceData = policySetMapping.ios || [];
          break;
        case 'iPad':
          deviceData = policySetMapping.ipados || [];
          break;
        case 'Mac':
          deviceData = policySetMapping.macos || [];
          break;
        default:
          deviceData = policySetMapping.ios || [];
      }
      return { ios: deviceData };
    }

    return {};
  };

  const onPolicyCategoryChange = (value: any) => {
    setSelectedPolicyCategory(value);
  };

  const onDepartmentChange = (value: any) => {
    setSelectedDepartment(value);
  };

  // Auto-select first policy category when device type changes
  useEffect(() => {
    if (!isAdvancedHierarchy) {
      return;
    }
    const currentMapping: any = getCurrentPolicySetMapping();
    let deviceKey = '';

    if (activeView === 'ANDROID') {
      deviceKey = 'android';
    } else if (activeView === 'IOS') {
      deviceKey = 'ios'; // Always 'ios' key regardless of iPhone/iPad/Mac
    }

    if (
      deviceKey &&
      currentMapping[deviceKey] &&
      currentMapping[deviceKey].length > 0
    ) {
      const firstCategory = currentMapping[deviceKey][0];
      const firstCategoryId = firstCategory?.policyCategory?.policyCategoryId;

      if (firstCategoryId) {
        onPolicyCategoryChange(firstCategoryId);
      }
    }
  }, [activeView, selectedDevice, policySetMapping, isAdvancedHierarchy]); // Re-run when device type or data changes

  useEffect(() => {
    if (
      showSiteSelector &&
      siteOptions.length > 0 &&
      !selectedSiteId &&
      !sitesLoading
    ) {
      setSelectedSiteId(siteOptions[0].value);
    }
  }, [showSiteSelector, siteOptions, selectedSiteId, sitesLoading]);

  // Reset QR display when any QR-affecting input changes
  useEffect(() => {
    resetQRDisplay();
  }, [
    activeView,
    selectedDevice,
    enrollmentType,
    selectedPolicyCategory,
    selectedDepartment,
    selectedRequestor,
    selectedSiteId,
    resetQRDisplay
  ]);

  // Helper function to validate required fields based on product type
  const isValidationFailed = () => {
    // Base validation based on product type
    const baseValidationFailed = isMSPProduct
      ? !client?.accountId || !siteId // MSP: Both client?.accountId and siteId are mandatory
      : !siteId; // ITSM: Only siteId is mandatory

    // Additional validation for advanced hierarchy - Device Category value must be selected
    const deviceCategoryValidationFailed =
      isAdvancedHierarchy &&
      (!selectedPolicyCategory ||
        selectedPolicyCategory === null ||
        selectedPolicyCategory === '');

    return baseValidationFailed || deviceCategoryValidationFailed;
  };

  const handleGenerateQR = async () => {
    await generateEnrollmentQR({
      activeView,
      selectedDevice,
      enrollmentType,
      selectedPolicyCategory,
      selectedRequestor,
      client,
      siteId,
      selectedDepartment
    });
  };

  const handleSendEmail = async (emailAddresses: string[]) => {
    return await sendQRViaEmail(
      {
        activeView,
        selectedDevice,
        enrollmentType,
        selectedPolicyCategory,
        selectedRequestor,
        client,
        siteId,
        selectedDepartment
      },
      emailAddresses
    );
  };

  useEffect(() => {
    setQrLoading(true);
  }, [setQrLoading]);

  // Helper function to render the main content based on MDM configuration status
  const renderContent = () => {
    if (mdmConfigLoading) {
      return (
        <Card className="p20">
          <div className="p20 flex center min-height-200">
            <Loader />
          </div>
        </Card>
      );
    }

    if (activeView === 'IOS' && !hasIosConfig) {
      let url = '';
      if (isMSPProduct) {
        url = `/onboard/mdm-client-setup/${client?.accountId || ''}?tab=ios`;
      } else {
        url = `/onboard/mdm-client-setup?tab=ios`;
      }
      const handleOpenMDMSetup = () => {
        // @ts-ignore
        tabContext.openTab({
          module: 'ONBOARD',
          url: url
        });
      };
      return (
        <Banner status="warning">
          <p>
            <FormattedMessage id="mdm.config.not.added.warning" />{' '}
            <Button plain onClick={handleOpenMDMSetup}>
              <FormattedMessage id="mdm.config.add.action" />
            </Button>{' '}
            <FormattedMessage id="mdm.config.for.client" />
          </p>
        </Banner>
      );
    }

    if (activeView === 'ANDROID' && !hasAndroidConfig) {
      let url = '';
      if (isMSPProduct) {
        url = `/onboard/mdm-client-setup/${client?.accountId ||
          ''}?tab=android`;
      } else {
        url = `/onboard/mdm-client-setup?tab=android`;
      }
      const handleOpenMDMSetup = () => {
        // @ts-ignore
        tabContext.openTab({
          module: 'ONBOARD',
          url: url
        });
      };

      return (
        <Banner status="warning">
          <p>
            <FormattedMessage id="mdm.config.not.added.warning" />{' '}
            <Button plain onClick={handleOpenMDMSetup}>
              <FormattedMessage id="mdm.config.add.action" />
            </Button>{' '}
            <FormattedMessage id="mdm.config.for.client" />
          </p>
        </Banner>
      );
    }

    const getAssetViewURL = () => {
      if (activeView === 'IOS') {
        if (selectedDevice === 'iPhone') {
          return ASSET_VIEW_URL.APPLE_IOS;
        }
        if (selectedDevice === 'iPad') {
          return ASSET_VIEW_URL.APPLE_IPAD_OS;
        }
        if (selectedDevice === 'Mac') {
          return ASSET_VIEW_URL.ENDPOINT;
        }
      }
      return ASSET_VIEW_URL.ANDROID;
    };

    return (
      <div className="flex col g30 pb40">
        <div>
          <InstructionStep
            step={1}
            description={
              selectedDevice === 'Mac'
                ? 'mdm.enrollment.manual.mac.step1.description'
                : 'mdm.enrollment.manual.step1.description'
            }
            className="mb10"
          />
          <Card noShadow border="secondary">
            <div className="p20 flex col g20">
              {activeView === 'IOS' && (
                <DeviceTypeSelector
                  selectedDevice={selectedDevice}
                  onDeviceChange={setSelectedDevice}
                />
              )}

              {activeView === 'ANDROID' && (
                <EnrollmentTypeSelector
                  enrollmentType={enrollmentType}
                  onEnrollmentTypeChange={setEnrollmentType}
                />
              )}

              {showSiteSelector && (
                <SiteSelector
                  siteOptions={siteOptions}
                  loading={sitesLoading}
                  error={sitesError}
                  value={selectedSiteId}
                  onChange={setSelectedSiteId}
                />
              )}

              <EnrollmentForm
                siteId={siteId}
                inAdvancedHierarchy={isAdvancedHierarchy}
                policySetMapping={getCurrentPolicySetMapping()}
                selectedPolicyCategory={selectedPolicyCategory}
                onPolicyCategoryChange={onPolicyCategoryChange}
                handleDepartmentChange={onDepartmentChange}
                selectedDepartment={selectedDepartment}
                selectedOS={activeView}
                userAssociations={userAssociations}
                selectedRequestor={selectedRequestor}
                onRequestorChange={setSelectedRequestor}
                associationsLoading={associationsLoading}
              />

              <div>
                <Button
                  loading={generateQRLoading}
                  onClick={handleGenerateQR}
                  disabled={isValidationFailed()}
                  primary
                >
                  <FormattedMessage
                    id={
                      selectedDevice === 'Mac'
                        ? 'mdm.enrollment.profile.generate'
                        : 'mdm.enrollment.qr.generate'
                    }
                  />
                </Button>
              </div>
            </div>
          </Card>
        </div>

        <div>
          <InstructionStep
            step={2}
            description={
              selectedDevice === 'Mac'
                ? 'mdm.enrollment.manual.mac.step2.description'
                : 'mdm.enrollment.manual.step2.description'
            }
            className="mb10"
          />

          <QRCodeDisplay
            qrConfigData={qrConfigData}
            qrLoading={qrLoading}
            generateQRLoading={generateQRLoading}
            showQRCard={showQRCard}
            activeView={activeView}
            enrollmentType={enrollmentType}
            selectedDeviceType={selectedDevice}
            enrollmentUrl={enrollmentResponse?.enrollmentUrl}
            validationCode={enrollmentResponse?.validationCode}
          >
            <EmailSender
              onSendEmail={handleSendEmail}
              sendEmailLoading={sendEmailLoading}
              disabled={!qrConfigData}
            />
          </QRCodeDisplay>
        </div>

        {isConfigurationPage && (
          <div>
            <InstructionStep
              step={3}
              description="mdm.enrollment.manual.step3.description"
              className="mb10"
            />

            <div>
              <Button
                url={getAssetViewURL()}
                icon={
                  <div className="fx center ignoreOverride">
                    <SVGIcon name="ExternalUrl" />
                  </div>
                }
                secondary
              >
                <FormattedMessage id="mdm.assets.view.devices" />
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return <>{renderContent()}</>;
};

export default MDMManualEnrollment;
